import React, { useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Paper,
  Tooltip,
  List as MUIList,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useNavigate } from "react-router-dom";
import AthleteSearchCard from "./AthleteSearchCard";
import NoResultsFound from "./NoResultsFound";
import useBoardSelection from "../../../../hooks/useBoardSelection";
import BoardSelectionMenus from "../../../../components/common/BoardSelectionMenus";

const SearchResultsGrid = ({
  results = [],
  viewMode = "grid",
  isLoading = false,

  onAddToBoard,
  onToggleFavorite,
  onViewProfile,
  hasActiveFilters = false,
  onClearFilters,
  searchQuery = "",
}) => {
  const navigate = useNavigate();

  // Track viewed athletes
  const [viewedAthletes, setViewedAthletes] = useState([]);

  // Board selection hook for list view
  const boardSelection = useBoardSelection();

  // Current athlete for board selection in list view
  const [currentAthleteForBoard, setCurrentAthleteForBoard] = useState(null);

  // Notification state for list view
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Handle adding athlete to board (for grid view - passes through to AthleteSearchCard)
  const handleAddToBoard = useCallback(
    (athlete) => {
      onAddToBoard?.(athlete);
    },
    [onAddToBoard]
  );

  // Handle adding athlete to board for list view (opens board selection menu)
  const handleAddToBoardListView = useCallback(
    (event, athlete) => {
      setCurrentAthleteForBoard(athlete);
      boardSelection.handleBoardMenuClick(event);
    },
    [boardSelection]
  );

  // Handle board add success for list view
  const handleBoardAddSuccess = useCallback(
    (boardId, columnId, boardName, columnName) => {
      const message = `Added ${currentAthleteForBoard?.firstName} ${currentAthleteForBoard?.lastName} to ${boardName} - ${columnName}`;
      setNotification({
        open: true,
        message,
        severity: "success",
      });

      // Call the original onAddToBoard if provided
      if (onAddToBoard && currentAthleteForBoard) {
        onAddToBoard(currentAthleteForBoard);
      }
    },
    [onAddToBoard, currentAthleteForBoard]
  );

  // Handle navigate to board
  const handleNavigateToBoard = useCallback(
    (boardId) => {
      navigate(`/boards?boardId=${boardId}`);
    },
    [navigate]
  );

  // Handle close notification
  const handleCloseNotification = useCallback(() => {
    setNotification({ ...notification, open: false });
  }, [notification]);

  // Handle toggling favorite status
  const handleToggleFavorite = useCallback(
    (athlete) => {
      onToggleFavorite?.(athlete);
    },
    [onToggleFavorite]
  );

  // Handle viewing athlete profile
  const handleViewProfile = useCallback(
    (athlete) => {
      // Add to viewed athletes if not already viewed
      if (!viewedAthletes.includes(athlete.id)) {
        setViewedAthletes((prev) => [...prev, athlete.id]);
      }
      onViewProfile?.(athlete);
    },
    [onViewProfile, viewedAthletes]
  );

  // Format height from inches to feet and inches
  const formatHeight = (height) => {
    if (!height) return "N/A";
    // Convert string to number if needed
    const inches = parseInt(height);
    if (isNaN(inches)) return "N/A";
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  // Render loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading athletes...
        </Typography>
      </Box>
    );
  }

  // Render empty state
  if (results.length === 0) {
    return (
      <NoResultsFound
        searchQuery={searchQuery}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Grid view */}
      {viewMode === "grid" && (
        <Grid container spacing={2}>
          {results.map((athlete) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={athlete.id}>
              <AthleteSearchCard
                athlete={athlete}
                onAddToBoard={handleAddToBoard}
                onToggleFavorite={handleToggleFavorite}
                onViewProfile={handleViewProfile}
                viewed={viewedAthletes.includes(athlete.id)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* List view */}
      {viewMode === "list" && (
        <Paper elevation={1}>
          <MUIList sx={{ width: "100%", bgcolor: "background.paper" }}>
            {results.map((athlete, index) => (
              <React.Fragment key={athlete.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Add to Board">
                        <IconButton
                          edge="end"
                          onClick={(event) =>
                            handleAddToBoardListView(event, athlete)
                          }
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Profile">
                        <IconButton
                          edge="end"
                          onClick={() => handleViewProfile(athlete)}
                        >
                          <ViewListIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  sx={{
                    bgcolor: viewedAthletes.includes(athlete.id)
                      ? "action.hover"
                      : "inherit",
                    borderLeft: "none",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={`${athlete.firstName} ${athlete.lastName}`}
                      src={
                        athlete.profilePicture ||
                        `https://ui-avatars.com/api/?name=${athlete.firstName}+${athlete.lastName}&background=random`
                      }
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" component="span">
                        {athlete.firstName} {athlete.lastName}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          ({athlete.position})
                        </Typography>
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {athlete.highSchool}, {athlete.state}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatHeight(athlete.height)},{" "}
                          {athlete.weight ? `${athlete.weight} lbs` : "N/A"}
                          {athlete.fortyTime && `, 40yd: ${athlete.fortyTime}s`}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                {index < results.length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </MUIList>
        </Paper>
      )}

      {/* Board Selection Menus for List View */}
      <BoardSelectionMenus
        {...boardSelection}
        athlete={currentAthleteForBoard}
        onAddToBoard={(athlete, boardId, columnId) =>
          boardSelection.handleAddToBoard(
            athlete,
            boardId,
            columnId,
            handleBoardAddSuccess
          )
        }
        onCreateBoard={(athlete) =>
          boardSelection.handleCreateBoard(athlete, handleBoardAddSuccess)
        }
        onNavigateToBoard={handleNavigateToBoard}
      />

      {/* Notification Snackbar for List View */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchResultsGrid;
